import React from "react";
import {
  FaCheck,
  FaEdit,
  FaCopy,
  FaTrashAlt,
  FaBuilding,
  FaCalendarAlt,
  FaExclamationCircle,
  FaPaperclip,
} from "react-icons/fa";

const TaskListView = ({
  tasks,
  onEdit,
  onDuplicate,
  onDelete,
  onStatusChange,
}) => {
  const formatDueDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const isOverdue = (task) => {
    if (!task.due_date || task.status === "Completed") return false;
    const due = new Date(task.due_date);
    const now = new Date();
    return due < now;
  };

  const getPriorityBadgeClass = (priority) => {
    const p = (priority || "Medium").toLowerCase();
    if (p === "urgent") return "tm-badge tm-badge-priority-urgent";
    if (p === "high") return "tm-badge tm-badge-priority-high";
    if (p === "low") return "tm-badge tm-badge-priority-low";
    return "tm-badge tm-badge-priority-medium";
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "To Do").toLowerCase();
    if (s.includes("progress")) return "tm-badge tm-badge-status-inprogress";
    if (s.includes("review")) return "tm-badge tm-badge-status-inreview";
    if (s.includes("complete")) return "tm-badge tm-badge-status-completed";
    return "tm-badge tm-badge-status-todo";
  };

  if (tasks.length === 0) {
    return (
      <div
        className="tm-list-container"
        style={{ padding: "40px", textAlign: "center", color: "#64748b" }}
      >
        <p style={{ margin: 0, fontSize: "15px" }}>No tasks found matching your criteria.</p>
      </div>
    );
  }

  return (
    <div className="tm-list-container">
      <table className="tm-list-table">
        <thead>
          <tr>
            <th style={{ width: "40px", textAlign: "center" }}>Done</th>
            <th>Task Name</th>
            <th>Client Link</th>
            <th>Assigned To</th>
            <th>Priority</th>
            <th>Due Date</th>
            <th>Status</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const completed = task.status === "Completed";
            const overdue = isOverdue(task);

            return (
              <tr key={task.id}>
                {/* Complete checkbox button */}
                <td style={{ textAlign: "center" }}>
                  <button
                    onClick={() =>
                      onStatusChange(task.id, completed ? "In Progress" : "Completed")
                    }
                    style={{
                      width: "22px",
                      height: "22px",
                      borderRadius: "6px",
                      border: completed ? "none" : "2px solid #cbd5e1",
                      background: completed ? "#10b981" : "#ffffff",
                      color: "#ffffff",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "11px",
                      transition: "all 0.15s",
                    }}
                    title={completed ? "Mark Incomplete" : "Mark Completed"}
                  >
                    {completed && <FaCheck />}
                  </button>
                </td>

                {/* Title */}
                <td>
                  <div
                    onClick={() => onEdit(task)}
                    style={{
                      fontWeight: 600,
                      color: completed ? "#94a3b8" : "#1e293b",
                      textDecoration: completed ? "line-through" : "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>{task.title}</span>
                    {task.attachments && task.attachments.length > 0 && (
                      <span className="tm-attachment-badge" title={`${task.attachments.length} attachment(s)`}>
                        <FaPaperclip /> {task.attachments.length}
                      </span>
                    )}
                  </div>
                  {task.deal_reference && (
                    <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px" }}>
                      Deal: {task.deal_reference}
                    </div>
                  )}
                </td>

                {/* Client Link */}
                <td>
                  {task.client_name ? (
                    <span className="tm-client-badge">
                      <FaBuilding style={{ fontSize: "10px" }} />
                      {task.client_name}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8", fontSize: "12px" }}>Unlinked</span>
                  )}
                </td>

                {/* Assigned To */}
                <td>
                  <span style={{ fontWeight: 500, color: "#334155" }}>
                    {task.assigned_to || <span style={{ color: "#94a3b8" }}>Unassigned</span>}
                  </span>
                </td>

                {/* Priority */}
                <td>
                  <span className={getPriorityBadgeClass(task.priority)}>
                    {task.priority || "Medium"}
                  </span>
                </td>

                {/* Due Date */}
                <td>
                  <span
                    className={`tm-due-date ${overdue ? "overdue" : ""}`}
                    style={{ fontSize: "13px" }}
                  >
                    {overdue ? <FaExclamationCircle /> : <FaCalendarAlt />}
                    {formatDueDate(task.due_date)}
                  </span>
                </td>

                {/* Status Dropdown / Quick switcher */}
                <td>
                  <select
                    value={task.status || "To Do"}
                    onChange={(e) => onStatusChange(task.id, e.target.value)}
                    style={{
                      padding: "4px 8px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "12px",
                      fontWeight: 600,
                      background: "#ffffff",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value="To Do">To Do</option>
                    <option value="In Progress">In Progress</option>
                    <option value="In Review">In Review</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                  </select>
                </td>

                {/* Actions */}
                <td style={{ textAlign: "right" }}>
                  <div style={{ display: "inline-flex", gap: "6px" }}>
                    <button
                      className="tm-card-menu-btn"
                      title="Edit Task"
                      onClick={() => onEdit(task)}
                    >
                      <FaEdit />
                    </button>
                    <button
                      className="tm-card-menu-btn"
                      title="Duplicate Task"
                      onClick={() => onDuplicate(task)}
                    >
                      <FaCopy />
                    </button>
                    <button
                      className="tm-card-menu-btn"
                      title="Delete Task"
                      style={{ color: "#ef4444" }}
                      onClick={() => onDelete(task.id)}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TaskListView;
